import React, { FC } from "react";

import { useTranslation } from "react-i18next";

import { Status } from "@/components/atoms";
import { StatusColorType } from "@/components/atoms/Status/styled";
import { DetailPanel, DetailPanels } from "@/components/organisms";
import { BrokerState, InsuredGood, Permissions } from "@/shared/domain";
import { useAuth, useSecuredUrl } from "@/shared/hooks";
import { useNavigation } from "@/shared/hooks/useNavigation";

import {
    panelCircumstancesSchema,
    panelConcernedsSchema,
    panelExpertiseSchema,
    panelManagerSchema,
    panelSettlementSchema,
    panelStakeholdersSchema,
} from "./panelSchema";
import { StyledClaimFolder, StyledDetailPanelStakeholders } from "./styled";

type ClaimDetailsPanelProps = {
    ibpAclId: number;
    claimDetails: MyBrokerApi.ClaimDetails;
};

const AccordionIndex = {
    CIRCUMSTANCES: "circumstances",
    STAKEHOLDERS: "stakeholders",
    CONCERNEDS: "concerneds",
    EXPERTISE: "expertise",
    MANAGER: "manager",
    SETTLEMENT: "settlement",
};

const ClaimDetailsPanel: FC<ClaimDetailsPanelProps> = ({ claimDetails, ibpAclId }) => {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const { onSecureUrlClick } = useSecuredUrl();
    const { navToInsuredGoodDetail } = useNavigation();

    const folderDetailsWrapper = (folder, index) => (
        <>
            {folder.folderStakeholders && folder.folderStakeholders.length > 0 && (
                <StyledDetailPanelStakeholders
                    id={`${AccordionIndex.STAKEHOLDERS}${index}`}
                    key={`stakeholders-${folder.folderId}`}
                    title={t("Common.Stakeholders")}
                    infoList={panelStakeholdersSchema(onSecureUrlClick, folder.folderStakeholders)}
                />
            )}
            {folder.insuredGoods.map((concerned, concernedIndex) => (
                <DetailPanel
                    detailPanelInfoGroup={folder.insuredGoods.length > 1}
                    lastDetailPanelInGroup={
                        folder.insuredGoods.length > 1 && concernedIndex === folder.insuredGoods.length - 1
                    }
                    id={`${AccordionIndex.CONCERNEDS}${index}`}
                    key={`concerneds-${concerned.riskObjectId}`}
                    title={concernedIndex === 0 && t("Common.Concerns")}
                    onClick={
                        !folder.isReadOnly
                            ? () => {
                                  navToInsuredGoodDetail({
                                      id: concerned.riskObjectId,
                                      type: concerned.insuredGoodType,
                                      canRequestGreenCard: concerned.canRequestGreenCard,
                                      policyholderId: claimDetails.policyholderId,
                                      ibpAclId,
                                  } as InsuredGood);
                              }
                            : undefined
                    }
                    infoList={panelConcernedsSchema(concerned, index, i18n.language)}
                />
            ))}
            {folder.folderExpertise && (
                <DetailPanel
                    id={`${AccordionIndex.EXPERTISE}${index}`}
                    title={t("Common.Expertise")}
                    infoList={panelExpertiseSchema(folder.folderExpertise, i18n.language)}
                />
            )}
            {user.hasPermission(Permissions.ViewClaimsPayments, claimDetails.policyholderId) &&
                folder.folderSettlements && (
                    <DetailPanel
                        id={`${AccordionIndex.SETTLEMENT}${index}`}
                        title={t("Common.Payments")}
                        infoList={panelSettlementSchema(folder.folderSettlements, index, i18n.language)}
                    />
                )}
        </>
    );

    if (!claimDetails) return <> </>;

    return (
        <DetailPanels defaultDetailPanelOpen={AccordionIndex.CIRCUMSTANCES}>
            <DetailPanel
                id={AccordionIndex.CIRCUMSTANCES}
                title={t("Common.Circumstances")}
                infoList={panelCircumstancesSchema(claimDetails.circumstances)}
            />
            {claimDetails.folders.length > 1
                ? claimDetails.folders.map((folder, index) => (
                      <StyledClaimFolder key={`folder-${folder.folderId}`}>
                          <div className="folderTitle">
                              <p>{`${folder.companyName} - ${folder.contractReference} - ${folder.policyholderFullName}`}</p>
                              <Status
                                  text={folder.folderState.name}
                                  color={
                                      folder.folderState.id === BrokerState.ON_GOING
                                          ? StatusColorType.Info
                                          : StatusColorType.Success
                                  }
                              />
                          </div>
                          {folderDetailsWrapper(folder, index)}
                      </StyledClaimFolder>
                  ))
                : folderDetailsWrapper(claimDetails.folders[0], 0)}

            {user.hasPermission(Permissions.ViewClaimManager, claimDetails.policyholderId) && (
                <DetailPanel
                    id={AccordionIndex.MANAGER}
                    title={t("Common.Manager")}
                    infoList={panelManagerSchema(claimDetails.manager)}
                />
            )}
        </DetailPanels>
    );
};

export default ClaimDetailsPanel;
